import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
} from "../components/ui/field"
import { cn } from "../lib/utils"

export function FieldRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()

  const orientation = (element.variant ?? "vertical") as
    | "vertical"
    | "horizontal"

  // Compound mode: children include field-label, field-description, etc.
  const hasCompoundChildren = element.children?.some(
    (c) => c.type === "field-label" || c.type === "field-content" || c.type === "field-description"
  )

  // Compound mode: children are field-label, field-description, etc.
  // Slot renderers handle each child type.
  if (hasCompoundChildren) {
    return (
      <Field orientation={orientation} className={cn(element.className)} data-testid={`field-${element.id}`}>
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Field>
    )
  }

  // Legacy mode: label/description as props
  const label = element.text
    ? String(resolveSymbol(element.text) ?? "")
    : ""
  const description = element.description
    ? String(resolveSymbol(element.description) ?? "")
    : ""

  if (orientation === "horizontal") {
    return (
      <Field
        orientation={orientation}
        className={cn(element.className)}
        data-testid={`field-${element.id}`}
      >
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
        <FieldContent>
          {label && <FieldLabel>{label}</FieldLabel>}
          {description && <FieldDescription>{description}</FieldDescription>}
        </FieldContent>
      </Field>
    )
  }

  return (
    <Field
      orientation={orientation}
      className={cn(element.className)}
      data-testid={`field-${element.id}`}
    >
      {label && <FieldLabel>{label}</FieldLabel>}
      {element.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
      {description && <FieldDescription>{description}</FieldDescription>}
    </Field>
  )
}
